/**
 * NotifyCXDialog - Contact CX about an OOS issue
 *
 * Two modes (BLU-386):
 * - **Send email** (default) — pick a remedy template, preview, send via Resend.
 * - **Mark manually** — record that CX was contacted outside the app
 *   (Gorgias, phone, custom email) without sending anything. Optional note
 *   captures what the user did.
 *
 * Both modes write `metadata.cx_notified_at` so the existing OOS "Contacted"
 * filter picks them up. The pushed `cx_notifications[]` entry carries
 * `method: 'template' | 'manual'` for provenance.
 *
 * The caller says which modes this user may use (`allowedModes`, from
 * `resolveContactAccess`). The server gates the two differently: the email is
 * coordinator-only, the manual mark is open to CX. With a single allowed mode
 * the picker is not rendered and the dialog opens straight on that form. When
 * that single mode is the manual mark, the dialog is titled "Mark as contacted"
 * (the entry button's own label), because nothing is sent to anyone.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Mail, Send, AlertTriangle, UserCheck } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import type { OrderDetail, ProductionIssue } from '../types';
import type { ContactMode } from '../contactAccess';

interface NotifyCXDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: string;
  issue: ProductionIssue;
  orderDetail: OrderDetail;
  /** The modes this user may use, in display order (see `resolveContactAccess`).
   *  The dialog opens on the first; the picker shows only when there are two. */
  allowedModes: readonly ContactMode[];
  /** Called after the customer was successfully contacted (email or manual mark). */
  onContacted?: () => void;
}

export function NotifyCXDialog({
  open,
  onOpenChange,
  issueId,
  issue,
  orderDetail,
  allowedModes,
  onContacted,
}: NotifyCXDialogProps) {
  const { t } = useTranslation('production');
  // The first allowed mode. The caller does not render the entry when the list
  // is empty; if it ever were, fall to the manual mark, which sends nothing.
  const firstMode: ContactMode = allowedModes[0] ?? 'mark_manually';
  const [selectedMode, setSelectedMode] = useState<ContactMode>(firstMode);
  // Never act in a mode this user is not allowed: if the allowed list changes
  // under an open dialog (role resolved late), snap back to the first allowed.
  const mode: ContactMode = allowedModes.includes(selectedMode) ? selectedMode : firstMode;
  const [selectedRemedy, setSelectedRemedy] = useState<string | null>(null);
  const [manualNote, setManualNote] = useState('');

  const utils = trpc.useUtils();

  // Fetch available remedies (email mode only, so never for a manual-only user)
  const remediesQuery = trpc.productionSettings.getCxRemedies.useQuery(undefined, {
    enabled: open && mode === 'send_email',
  });

  const resetForm = () => {
    setSelectedMode(firstMode);
    setSelectedRemedy(null);
    setManualNote('');
  };

  const notifyMutation = trpc.productionOrders.issues.notifyCX.useMutation({
    onSuccess: (data) => {
      toast.success(t('issues.notifyCx.emailSent', { email: data.sentTo }));
      utils.productionOrders.issues.invalidate();
      onOpenChange(false);
      resetForm();
      onContacted?.();
    },
    onError: (err) => {
      toast.error(err.message || t('issues.notifyCx.sendFailed'));
    },
  });

  const markManuallyMutation = trpc.productionOrders.issues.markCxContactedManually.useMutation({
    onSuccess: () => {
      toast.success(
        t('issues.markCxContacted.success', { order: orderDetail.order_name ?? '' })
      );
      utils.productionOrders.issues.invalidate();
      onOpenChange(false);
      resetForm();
      onContacted?.();
    },
    onError: (err) => {
      toast.error(err.message || t('issues.markCxContacted.failed'));
    },
  });

  const handleSubmit = () => {
    if (mode === 'send_email') {
      if (!selectedRemedy) return;
      notifyMutation.mutate({
        issue_id: issueId,
        remedy_value: selectedRemedy,
      });
    } else {
      const note = manualNote.trim();
      markManuallyMutation.mutate({
        issue_id: issueId,
        ...(note.length > 0 ? { note } : {}),
      });
    }
  };

  const remedies = remediesQuery.data?.remedies ?? [];
  const selectedRemedyObj = remedies.find(r => r.value === selectedRemedy);

  // Get affected (OOS) items
  const oosItems = orderDetail.line_items.filter(li => li.is_oos);
  const blockedMatch = issue.blocked_sku
    ? orderDetail.line_items.find(li => li.sku === issue.blocked_sku || li.shopify_sku === issue.blocked_sku)
    : undefined;
  const displayItems: Array<{ sku: string; product_name: string; quantity: number }> =
    oosItems.length > 0
      ? oosItems.map(li => ({ sku: li.sku, product_name: li.product_name || li.sku, quantity: li.quantity }))
      : issue.blocked_sku
        ? [{ sku: issue.blocked_sku, product_name: blockedMatch?.product_name || issue.blocked_sku, quantity: blockedMatch?.quantity ?? 1 }]
        : [];

  // Check if already notified
  const metadata = issue.metadata as Record<string, unknown> | null;
  const alreadyNotified = metadata?.cx_notified_at != null;

  const isEmailMode = mode === 'send_email';
  const isManualMode = mode === 'mark_manually';
  const isPending = notifyMutation.isPending || markManuallyMutation.isPending;

  const submitDisabled =
    isPending ||
    (isEmailMode && (!selectedRemedy || !orderDetail.customer_email));

  // A caller whose only mode is the manual mark (CX, a coordinator on another
  // org's row, a pure client's coordinator) notifies nobody from here, so the
  // dialog carries the name of the one thing it does: the same label as the
  // entry button that opened it.
  const isManualOnly = allowedModes.length === 1 && allowedModes[0] === 'mark_manually';

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) resetForm();
      }}
    >
      <AlertDialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {isManualOnly ? (
              <UserCheck className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Mail className="h-5 w-5" aria-hidden="true" />
            )}
            {t(isManualOnly ? 'issues.notifyCx.modeMarkManually' : 'issues.notifyCx.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isEmailMode
              ? t('issues.notifyCx.description', { count: displayItems.length })
              : t('issues.markCxContacted.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          {/* Mode picker (BLU-386) — matches the remedy-picker pattern below.
              Only when there is a choice to make: a user with one allowed mode
              lands directly on that form. */}
          {allowedModes.length > 1 && (
            <RadioGroup
              value={mode}
              onValueChange={(value) => setSelectedMode(value as ContactMode)}
              className="space-y-2"
            >
              <div className="flex items-start space-x-3 rounded-lg border p-3 hover:bg-muted/50 motion-safe:transition-colors">
                <RadioGroupItem value="send_email" id="cx-mode-send-email" className="mt-0.5" />
                <Label htmlFor="cx-mode-send-email" className="flex-1 cursor-pointer space-y-1">
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <Send className="h-3.5 w-3.5" />
                    {t('issues.notifyCx.modeSendEmail')}
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t('issues.notifyCx.modeSendEmailDesc')}
                  </p>
                </Label>
              </div>
              <div className="flex items-start space-x-3 rounded-lg border p-3 hover:bg-muted/50 motion-safe:transition-colors">
                <RadioGroupItem value="mark_manually" id="cx-mode-manual" className="mt-0.5" />
                <Label htmlFor="cx-mode-manual" className="flex-1 cursor-pointer space-y-1">
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <UserCheck className="h-3.5 w-3.5" />
                    {t('issues.notifyCx.modeMarkManually')}
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t('issues.notifyCx.modeMarkManuallyDesc')}
                  </p>
                </Label>
              </div>
            </RadioGroup>
          )}

          {/* Already notified warning */}
          {alreadyNotified && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              <span className="text-sm text-amber-800 dark:text-amber-200">
                {isEmailMode
                  ? t('issues.notifyCx.alreadyNotified')
                  : t('issues.markCxContacted.alreadyMarked')}
              </span>
            </div>
          )}

          {/* Customer / order info — shared across both modes */}
          <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
            {/* Email mode shows To: prominently; manual mode shows it muted when present */}
            {isEmailMode ? (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{t('issues.notifyCx.to')}</span>
                <span className="text-sm font-mono">{orderDetail.customer_email || t('issues.notifyCx.noEmail')}</span>
              </div>
            ) : orderDetail.customer_email ? (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('issues.notifyCx.customer')}</span>
                <span className="text-sm font-mono text-muted-foreground">{orderDetail.customer_email}</span>
              </div>
            ) : null}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('issues.notifyCx.order')}</span>
              <span className="text-sm">{orderDetail.order_name}</span>
            </div>
          </div>

          {/* Affected items */}
          {displayItems.length > 0 && (
            <div className="space-y-2">
              <span className="text-sm font-medium">{t('issues.notifyCx.affectedItems')}</span>
              <div className="space-y-1">
                {displayItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm rounded border px-3 py-2 bg-red-50 dark:bg-red-950/30">
                    <span>{item.product_name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{item.sku}</span>
                      <Badge variant="destructive" className="text-xs">OOS</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isEmailMode && (
            <>
              <Separator />

              {/* Remedy picker */}
              <div className="space-y-3">
                <span className="text-sm font-medium">{t('issues.notifyCx.selectRemedy')}</span>
                {remediesQuery.isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : remedies.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('issues.notifyCx.noRemedies')}
                  </p>
                ) : (
                  <RadioGroup
                    value={selectedRemedy ?? undefined}
                    onValueChange={setSelectedRemedy}
                    className="space-y-2"
                  >
                    {remedies.map((remedy) => (
                      <div
                        key={remedy.value}
                        className="flex items-start space-x-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                      >
                        <RadioGroupItem
                          value={remedy.value}
                          id={`remedy-${remedy.value}`}
                          className="mt-0.5"
                        />
                        <Label
                          htmlFor={`remedy-${remedy.value}`}
                          className="flex-1 cursor-pointer space-y-1"
                        >
                          <span className="font-medium text-sm">{remedy.label}</span>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {remedy.template}
                          </p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              </div>

              {/* Preview */}
              {selectedRemedyObj && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <span className="text-sm font-medium">{t('issues.notifyCx.emailPreview')}</span>
                    <div className="rounded-lg border bg-white dark:bg-gray-950 p-4 text-sm leading-relaxed space-y-3">
                      <p>Hi {orderDetail.customer_name?.split(/\s+/)[0] || 'there'},</p>
                      <p>Thanks for your order <strong>{orderDetail.order_name}</strong>.</p>
                      <p>
                        Unfortunately, the following item{displayItems.length > 1 ? 's are' : ' is'} currently
                        out of stock:
                      </p>
                      <ul className="list-disc pl-5 space-y-1">
                        {displayItems.map((item, idx) => (
                          <li key={idx}>
                            {item.product_name}
                          </li>
                        ))}
                      </ul>
                      <p>{selectedRemedyObj.template}</p>
                      <p className="text-muted-foreground">
                        If you have any questions, just reply to this email and we'll get back to you.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {isManualMode && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label htmlFor="manual-note" className="text-sm font-medium">
                  {t('issues.markCxContacted.noteLabel')}
                </Label>
                <Textarea
                  id="manual-note"
                  placeholder={t('issues.markCxContacted.notePlaceholder')}
                  value={manualNote}
                  onChange={(e) => setManualNote(e.target.value)}
                  maxLength={500}
                />
                {500 - manualNote.length <= 50 && (
                  <p
                    className={
                      500 - manualNote.length <= 20
                        ? 'text-xs text-amber-600 dark:text-amber-500'
                        : 'text-xs text-muted-foreground'
                    }
                  >
                    {t('issues.markCxContacted.noteHint', { count: 500 - manualNote.length })}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={resetForm}>
            {t('issues.notifyCx.cancel')}
          </AlertDialogCancel>
          <Button
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="h-14 gap-2"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isEmailMode ? (
              <Send className="h-4 w-4" />
            ) : (
              <UserCheck className="h-4 w-4" />
            )}
            {isEmailMode
              ? t('issues.notifyCx.sendEmail')
              : t('issues.markCxContacted.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
